import { AnalyticsErrorBoundary } from '../shared/AnalyticsErrorBoundary';
import { SkeletonLoader } from '../shared/SkeletonLoader';
import { WellnessFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useWellnessKPIData } from '../../../hooks/analytics/useWellnessMetrics.tsx';
import { MetricCard } from '../overview/MetricCard';
import { CSSProperties, useRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  getFormName,
  useSelectedWellnessForm,
} from '../../../hooks/analytics/useSelectedWellnessForm';
import { FormScopePill } from './FormScopePill';

interface WellnessMetricsProps {
  filters: WellnessFilter;
}

function WellnessMetricsContent({ filters }: WellnessMetricsProps) {
  const isNarrow = useIsMobile(1024);
  const { kpiItems, isLoading } = useWellnessKPIData(filters);
  const { forms, selected } = useSelectedWellnessForm(filters);
  const prefersReducedMotion = useReducedMotion();
  const previousFormIdRef = useRef<number | null>(null);
  const switchedToFormIdRef = useRef<number | null>(null);
  if (selected && selected.formid !== previousFormIdRef.current) {
    if (previousFormIdRef.current !== null) switchedToFormIdRef.current = selected.formid;
    previousFormIdRef.current = selected.formid;
  }
  const animateFormChange =
    !prefersReducedMotion && selected !== null && selected.formid === switchedToFormIdRef.current;

  if (isLoading) {
    return <SkeletonLoader variant="metrics" count={4} />;
  }

  return (
    <div
      className={`grid gap-3 ${isNarrow ? 'grid-cols-2' : 'grid-cols-4'}`}
      style={{ '--anl-metric-bg': 'var(--anl-surface)' } as CSSProperties}
    >
      {kpiItems.map(item => {
        const showFormScope = item.isFormScoped && selected && forms.length > 1;
        const animateText = (text: string | undefined) =>
          showFormScope ? (
            <motion.span
              key={`${selected.formid}-${text}`}
              className="inline-block"
              initial={animateFormChange ? { opacity: 0, y: 6 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
            >
              {text}
            </motion.span>
          ) : (
            text
          );
        return (
          <MetricCard
            key={item.id}
            value={animateText(item.value)}
            label={
              showFormScope ? (
                <span className="-mr-7 flex flex-wrap items-center justify-between gap-1.5">
                  {item.label}
                  <FormScopePill name={getFormName(selected)} />
                </span>
              ) : (
                item.label
              )
            }
            secondaryValue={
              item.secondaryValue ? animateText(item.secondaryValue.value) : undefined
            }
            secondaryLabel={item.secondaryValue?.label}
            accent={String(item.color)}
            preserveSecondarySpacing
          />
        );
      })}
    </div>
  );
}

export function WellnessMetrics({ filters }: WellnessMetricsProps) {
  return (
    <AnalyticsErrorBoundary>
      <WellnessMetricsContent filters={filters} />
    </AnalyticsErrorBoundary>
  );
}
